/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { emailRegex, ERRORS, phoneRegex, ROUTE_PATHS } from '@/constants';
import type { IBaseForm, ICompany } from '@/types';
import { hasEmptyFields, hasFormAnyError } from '@/utils';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  Heading,
  Link,
  useControllableState,
} from '@chakra-ui/react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { shouldRequireVat } from '../../constants';
import { AddressFields, MbeInput } from '../shared';
import { useUserApi, useValidator } from '@/features/users';

const initialRequiredFields = ['firstName', 'lastName', 'company', 'email', 'phone', 'countryCode'];

export const CompanyForm: React.FC<IBaseForm<ICompany>> = ({
  defaultValues,
  onSubmit: providedOnSubmit,
}): JSX.Element => {
  const methods = useForm<ICompany>({ mode: 'onBlur', defaultValues });
  const userApi = useUserApi();
  const [isMailUsed, setIsMailUsed] = useControllableState({ defaultValue: false });

  const {
    formState: { errors },
    getValues,
    handleSubmit: submitHandler,
    register,
    trigger,
    watch,
  } = methods;

  const { t } = useTranslation();
  const { validateVatNumber } = useValidator();
  const isVatRequired = shouldRequireVat(watch('countryCode'));

  const requiredFields = isVatRequired
    ? [...initialRequiredFields, 'vatNumber']
    : initialRequiredFields;

  const areRequiredFieldsEmpty = hasEmptyFields<ICompany, Extract<keyof ICompany, string>>(
    watch(),
    requiredFields as unknown as Extract<keyof ICompany, string>[]
  );

  const isDisabled = areRequiredFieldsEmpty || isMailUsed || hasFormAnyError(errors);

  const handleSubmit: SubmitHandler<ICompany> = (data, event): void => {
    event?.preventDefault();

    const updatedCompanyInfo = { ...data, customerType: 'BUSINESS' };

    providedOnSubmit?.(updatedCompanyInfo);
  };

  const countryCodeValue = getValues('countryCode') && getValues('countryCode').toLowerCase();

  const vatNumberProps = register('vatNumber', {
    validate: (value) => validateVatNumber({ countryCode: countryCodeValue, vatNumber: value }),
  });

  return (
    <FormProvider {...methods}>
      <form
        autoComplete="off"
        id="companyForm"
        name="companyForm"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={submitHandler(handleSubmit)}
      >
        <Flex direction="column" gap={12} mb={8}>
          <MbeInput
            error={errors.firstName}
            id="firstName"
            placeholder={t('FORM.FIRSTNAME')}
            {...register('firstName', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <MbeInput
            error={errors.lastName}
            id="lastName"
            placeholder={t('FORM.LASTNAME')}
            {...register('lastName', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <MbeInput
            error={errors.company}
            id="company"
            placeholder={t('FORM.COMPANY')}
            {...register('company', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <MbeInput
            error={errors.vatNumber}
            id="vatNumber"
            placeholder={t('FORM.VAT')}
            {...vatNumberProps}
          />
          <MbeInput
            error={errors.email}
            id="email"
            placeholder={t('FORM.EMAIL')}
            {...register('email', {
              required: { value: true, message: t(ERRORS.empty) },
              pattern: { value: emailRegex, message: t(ERRORS.email) },
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onBlur: async (): Promise<void> => {
                const isMailKnown: boolean = await userApi.checkUserEmail(getValues('email'));
                setIsMailUsed(isMailKnown);
              },
            })}
          />
          {isMailUsed && (
            <Alert my={-8} status="error">
              <AlertIcon />
              <AlertDescription>
                <Trans
                  components={{
                    url: <Link color="brand.red.light" href={ROUTE_PATHS.userLogin} />,
                  }}
                  i18nKey="ERRORS.ALREADY_USED_EMAIL"
                />
              </AlertDescription>
            </Alert>
          )}
          <MbeInput
            error={errors.phone}
            id="phone"
            placeholder={t('FORM.PHONE')}
            {...register('phone', {
              required: { value: true, message: t(ERRORS.empty) },
              pattern: { value: phoneRegex, message: t(ERRORS.phoneNumber) },
            })}
          />
          <Flex direction="column">
            <Heading fontSize="large" fontWeight="bold" mb={4}>
              {t('WIZARD.USER.ADDRESS_FIELDS_TITLE')}
            </Heading>
            <Flex direction="column" gap={12}>
              <AddressFields
                onCountryChange={(): void => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  trigger('vatNumber');
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex flexDirection="row-reverse" gap={2} justifyContent="space-between">
          <Button disabled={isDisabled} type="submit" variant="mbePrimary">
            {t('ACTIONS.NEXT')}
          </Button>
        </Flex>
      </form>
    </FormProvider>
  );
};
